export type WarningSeverity = 'hard' | 'peer'

export type WarningField =
  | 'salesGoal'
  | 'profitMargin'
  | 'workingWeeks'
  | 'crewHoursPerWeek'
  | 'ownerSalary'
  | 'ownerHoursPerWeek'
  | 'workerHoursPerWeek'
  | 'fieldWorkerPay'
  | 'adminWorkerPay'
  | 'equipmentSalvage'
  | 'equipmentLife'
  | 'revenueScale'
  | 'goalVsPayroll'
  | 'goalVsCosts'
  | 'adminWagesShare'
  | 'adminLoad'
  | 'equipmentShare'

export type Coaching = {
  objection: string
  body: string[]
}

export type FieldWarning = {
  field: WarningField
  severity: WarningSeverity
  text: string
  coaching?: Coaching
}

export type Band = {
  low: number
  mid: number
  high: number
}

export const FALLBACK_BANDS: Record<'ownerSalary' | 'fieldWorkerPay' | 'crewHoursPerWeek' | 'workingWeeks', Band> = {
  ownerSalary: { low: 45000, mid: 85000, high: 150000 },
  fieldWorkerPay: { low: 15, mid: 22, high: 35 },
  crewHoursPerWeek: { low: 30, mid: 40, high: 60 },
  workingWeeks: { low: 20, mid: 39, high: 52 }
}

export const CREW_WEEK_CEILING = 70
export const OWNER_WEEK_CEILING = 60
export const OWNER_WEEK_IMPOSSIBLE = 90
export const OWNER_PAY_MIN_HOURS_PER_WEEK = 15
export const OWNER_SALARY_TYPO_CEILING = 1000000
export const OWNER_SALARY_PEER_HIGH = 250000
export const FEDERAL_HOURLY_FLOOR = 7.25
export const MARGIN_BAND: Band = { low: 10, mid: 20, high: 25 }
export const MARGIN_CEILING = 30
export const GOAL_SEASON_FLOOR = 25000
export const GOAL_ANNUAL_CONFIRM_CEILING = 200000
export const GOAL_TYPO_CEILING = 10000000
export const SEASON_WEEKS_CEILING = 52
export const SEASON_WEEKS_FLOOR = 6
export const EQUIPMENT_LIFE_CEILING = 40
export const EQUIPMENT_LIFE_CALENDAR_YEAR = 1900
export const ASSUMED_OWNER_HOURS_PER_WEEK = 40

const OWNER_PAY_FLOOR = FALLBACK_BANDS.ownerSalary.low

export const money = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`

const moneyRate = (n: number): string => `$${n.toFixed(2)}`

const whole = (n: number): string => `${Math.round(n)}`

const range = (low: number, high: number, format: (n: number) => string): string =>
  `${format(low)} to ${format(high)}`

const outsideBand = (value: number, band: Band): boolean => {
  return value < band.low || value > band.high
}

const PUT_IN_WHAT_ITS_WORTH =
  "Put in what you'd have to pay somebody to do your job. You don't have to take it home yet. But your price has to cover it."

const salesGoalWarning = (value: number): FieldWarning | null => {
  if (value > 0 && value < GOAL_SEASON_FLOOR) {
    return {
      field: 'salesGoal',
      severity: 'peer',
      text: "We're looking for your estimated revenue for an entire season, not a per job or per month amount. Is this actually your estimated revenue for the season?"
    }
  }
  if (value >= GOAL_SEASON_FLOOR && value < GOAL_ANNUAL_CONFIRM_CEILING) {
    return {
      field: 'salesGoal',
      severity: 'peer',
      text: 'Just checking — is that your revenue for the whole 12 months? This box wants the full year, not one month.',
      coaching: {
        objection: "Yes, that's my whole year",
        body: [
          "Then you're set — keep going.",
          "We ask because a monthly number in this box throws every step after it off by 12x, and it's a lot easier to catch here than at the end."
        ]
      }
    }
  }
  if (value >= GOAL_TYPO_CEILING) {
    return {
      field: 'salesGoal',
      severity: 'peer',
      text: 'Extra zero in your input? If not, cool, keep moving.'
    }
  }
  return null
}

const profitMarginWarning = (value: number): FieldWarning | null => {
  if (value < MARGIN_BAND.low) {
    return {
      field: 'profitMargin',
      severity: 'peer',
      text: `That's a thin cushion — one breakdown or one slow month could eat the whole year's profit. Aim for at least ${MARGIN_BAND.low}%.`
    }
  }
  if (value > MARGIN_CEILING) {
    return {
      field: 'profitMargin',
      severity: 'hard',
      text: "Not many guys clear that much net. If you're thinking of what's left after materials and labor, that's gross profit. Net profit is what is left after all your overhead is covered too.",
      coaching: {
        objection: 'But I do keep that much',
        body: [
          `You might well be keeping ${whole(value)}% of every job. Plenty of good companies do.`,
          "That's usually gross profit. It's what's left once you've covered materials and the crew. Net is what's left after everything else too: your own pay, the truck, insurance, the phone bill.",
          `Put in the net number. If you're not sure what yours is, ${MARGIN_BAND.mid}% is a solid target and you can change it later.`
        ]
      }
    }
  }
  return null
}

const workingWeeksWarning = (value: number): FieldWarning | null => {
  if (value > SEASON_WEEKS_CEILING) {
    return {
      field: 'workingWeeks',
      severity: 'hard',
      text: 'A season can\'t exceed 52 weeks — check your season dates. (Remember to factor in unbillable time. If you plan for 2 weeks of lost productivity in a season due to bad weather, enter only 50 weeks.)'
    }
  }
  if (value > 0 && value < SEASON_WEEKS_FLOOR) {
    return {
      field: 'workingWeeks',
      severity: 'hard',
      text: 'A whole year of costs would land on those few weeks, which sends your rate through the roof.'
    }
  }
  return null
}

const crewHoursWarning = (value: number): FieldWarning | null => {
  if (value > CREW_WEEK_CEILING) {
    return {
      field: 'crewHoursPerWeek',
      severity: 'hard',
      text: "That's more than one person works in a week. Put in the hours per crew member, not the crew's total."
    }
  }
  const band = FALLBACK_BANDS.crewHoursPerWeek
  if (value > 0 && outsideBand(value, band)) {
    return {
      field: 'crewHoursPerWeek',
      severity: 'peer',
      text: `Most crews run ${range(band.low, band.high, whole)} hours a week.`
    }
  }
  return null
}

const workerHoursWarning = (value: number): FieldWarning | null => {
  if (value > CREW_WEEK_CEILING) {
    return {
      field: 'workerHoursPerWeek',
      severity: 'hard',
      text: "That's more than one person works in a week. Put in this person's own hours — the tool adds the crew up for you."
    }
  }
  const band = FALLBACK_BANDS.crewHoursPerWeek
  if (value > 0 && outsideBand(value, band)) {
    return {
      field: 'workerHoursPerWeek',
      severity: 'peer',
      text: `Most crews run ${range(band.low, band.high, whole)} hours a week per person.`
    }
  }
  return null
}

const belowMinimumWage = (value: number): boolean => {
  return value > 0 && value < FEDERAL_HOURLY_FLOOR
}

const minimumWageText = (value: number): string =>
  `${moneyRate(value)} an hour is below minimum wage. If that's a yearly salary, switch the pay type over.`

const fieldWorkerPayWarning = (value: number): FieldWarning | null => {
  if (belowMinimumWage(value)) {
    return { field: 'fieldWorkerPay', severity: 'hard', text: minimumWageText(value) }
  }
  const band = FALLBACK_BANDS.fieldWorkerPay
  if (value > 0 && outsideBand(value, band)) {
    return {
      field: 'fieldWorkerPay',
      severity: 'peer',
      text: `Most crews pay ${range(band.low, band.high, moneyRate)} an hour.`
    }
  }
  return null
}

const adminWorkerPayWarning = (value: number): FieldWarning | null => {
  if (belowMinimumWage(value)) {
    return { field: 'adminWorkerPay', severity: 'hard', text: minimumWageText(value) }
  }
  return null
}

const ownerHoursWarning = (value: number): FieldWarning | null => {
  if (value > OWNER_WEEK_CEILING) {
    return {
      field: 'ownerHoursPerWeek',
      severity: value > OWNER_WEEK_IMPOSSIBLE ? 'hard' : 'peer',
      text: `That's ${whole(value / 7)} hours a day, every day. Even if you truly are working that many hours a week right now, enter the hours per week you'd like to sustain long term at a year round average. If you enter too many hours a week here, the math will take your high input and calculate your rate at an amount that requires you to work that many hours just to hit your profit target. Err on the low side, not high side.`
    }
  }
  return null
}

const ownerSalaryWarning = (value: number, ownerHoursPerWeek: number): FieldWarning | null => {
  if (value >= OWNER_SALARY_TYPO_CEILING) {
    return {
      field: 'ownerSalary',
      severity: 'hard',
      text: 'Looks like an extra zero? This box wants your yearly pay.'
    }
  }
  if (ownerHoursPerWeek < OWNER_PAY_MIN_HOURS_PER_WEEK) {
    return null
  }
  const band = FALLBACK_BANDS.ownerSalary
  const typical = range(OWNER_PAY_FLOOR, band.high, money)
  if (value <= 0) {
    return {
      field: 'ownerSalary',
      severity: 'hard',
      text: "Leaving this at nothing means your customers get you for free. Enter the amount you'd like to plan to pay yourself, even if that can't happen immediately.",
      coaching: {
        objection: "But I don't pay myself",
        body: [
          "Lots of owners don't pay themselves. Especially starting out.",
          "Here's the thing — this input influences your hourly rate that you'll use for estimating jobs. Put in nothing, and the math says your time is worth nothing. Your rate comes out too low, and every hour you work is an hour your customer got free.",
          PUT_IN_WHAT_ITS_WORTH
        ]
      }
    }
  }
  if (value < OWNER_PAY_FLOOR) {
    return {
      field: 'ownerSalary',
      severity: 'peer',
      text: `Most owners running ${whole(ownerHoursPerWeek)} hours a week put in ${typical}. Enter what you'd like to pay yourself, not what you take home today. That's how your rate comes out high enough to actually pay you.`,
      coaching: {
        objection: "But that's what I can afford",
        body: [
          'Lots of owners pay themselves less than the job is worth. Especially starting out.',
          "Here's the thing — this input influences your hourly rate that you'll use for estimating jobs. Pay yourself too little, and your rate comes out too low.",
          PUT_IN_WHAT_ITS_WORTH
        ]
      }
    }
  }
  if (value > OWNER_SALARY_PEER_HIGH) {
    return {
      field: 'ownerSalary',
      severity: 'peer',
      text: `Most owners running ${whole(ownerHoursPerWeek)} hours a week pay themselves ${typical}. If yours really is bigger, good for you — just check for an extra zero.`
    }
  }
  return null
}

export type FieldContext = {
  ownerHoursPerWeek?: number | null
}

export const checkField = (
  field: WarningField,
  value: number | null | undefined,
  context: FieldContext = {}
): FieldWarning | null => {
  if (value === null || value === undefined || !isFinite(value)) {
    return null
  }
  switch (field) {
    case 'salesGoal':
      return salesGoalWarning(value)
    case 'profitMargin':
      return profitMarginWarning(value)
    case 'workingWeeks':
      return workingWeeksWarning(value)
    case 'crewHoursPerWeek':
      return crewHoursWarning(value)
    case 'workerHoursPerWeek':
      return workerHoursWarning(value)
    case 'fieldWorkerPay':
      return fieldWorkerPayWarning(value)
    case 'adminWorkerPay':
      return adminWorkerPayWarning(value)
    case 'ownerHoursPerWeek':
      return ownerHoursWarning(value)
    case 'ownerSalary':
      return ownerSalaryWarning(value, context.ownerHoursPerWeek ?? ASSUMED_OWNER_HOURS_PER_WEEK)
    default:
      return null
  }
}

export type EquipmentRow = {
  ownershipType: 'Own' | 'Lease/Finance'
  replacementCost: number | null
  sellPrice: number | null
  avgUsefulLife: number | null
}

const equipmentLifeText = (years: number): string => {
  if (years >= EQUIPMENT_LIFE_CALENDAR_YEAR) {
    return 'That reads like a year on the calendar. This box wants how many years until you replace it.'
  }
  if (years > EQUIPMENT_LIFE_CEILING) {
    return "That's longer than anybody runs a machine — this box wants the years until you replace it. Its lifespan with your company."
  }
  return 'It needs at least a year of life — this box wants the years until you replace it.'
}

export const checkEquipmentRow = (row: EquipmentRow): FieldWarning | null => {
  if (row.ownershipType === 'Lease/Finance') {
    return null
  }
  const { replacementCost, sellPrice, avgUsefulLife } = row
  if (replacementCost !== null && sellPrice !== null && replacementCost > 0 && sellPrice > replacementCost) {
    return {
      field: 'equipmentSalvage',
      severity: 'hard',
      text: `You said this sells for ${money(sellPrice)} at the end of its life, but costs ${money(replacementCost)} to replace. Both of those can't be true at the same time — which one's wrong?`
    }
  }
  if (avgUsefulLife !== null && (avgUsefulLife <= 0 || avgUsefulLife > EQUIPMENT_LIFE_CEILING)) {
    return {
      field: 'equipmentLife',
      severity: 'hard',
      text: equipmentLifeText(avgUsefulLife)
    }
  }
  return null
}
